import type { CSSProperties, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'

export type MediaLight = 'left' | 'right' | 'top'

interface MediaProps {
    src: string
    alt: string
    sizes: string
    className?: string
    preload?: boolean
    // Darkens the picture into the site's look: black and white, lit by one warm amber
    // light from this side, falling off into deep black
    grade?: MediaLight
}

const OPTIMIZABLE_HOST = /(^|\.)cdn\.modulify\.website$/i

const LIGHT_POSITION: Record<MediaLight, string> = {
    left: '24% 30%',
    right: '76% 26%',
    top: '50% 8%'
}

const canOptimize = (src: string): boolean => {
    if (!src.startsWith('https://')) return false

    try {
        return OPTIMIZABLE_HOST.test(new URL(src).hostname)
    }
    catch {
        return false
    }
}

const Picture = ({ src, alt, sizes, className, preload = false }: Omit<MediaProps, 'grade'>): ReactNode => {
    if (canOptimize(src)) {
        return (
            <Image
                src={src}
                alt={alt}
                fill
                sizes={sizes}
                preload={preload}
                className={cn('object-cover', className)}
            />
        )
    }

    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={src}
            alt={alt}
            loading={preload ? 'eager' : 'lazy'}
            decoding='async'
            className={cn('absolute inset-0 h-full w-full object-cover', className)}
        />
    )
}

// Fills its positioned parent. Images in the site's own storage are resized and
// converted on the fly; anything pasted in from elsewhere still renders as a plain image.
export const Media = ({ grade, className, ...rest }: MediaProps): ReactNode => {
    if (!grade) return <Picture className={className} {...rest} />

    return (
        <div className='absolute inset-0 isolate overflow-hidden'>
            <Picture className={cn('grayscale contrast-[1.3] brightness-[0.82]', className)} {...rest} />

            <div
                aria-hidden='true'
                style={{ '--light': LIGHT_POSITION[grade] } as CSSProperties}
                className='umbra-light pointer-events-none absolute inset-0'
            />
        </div>
    )
}
